import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api, qs } from '../api/client';
import { useJobDrawer } from '../App';
import { fmtDate, STATUS_LABEL, partOf } from '../lib/format';

interface Results {
  jobs: { id: string; reference: string; name: string; productCode: string | null; salesOrderRef: string | null; status: string; requiredDate: string | null }[];
  operations: { id: string; name: string; status: string; jobId: string; reference: string; productCode: string | null }[];
  resources: { id: string; name: string; initials: string; active: boolean }[];
}

export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const drawer = useJobDrawer();
  const nav = useNavigate();
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') { e.preventDefault(); box.current?.querySelector('input')?.focus(); } };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, []);
  const res = useQuery({ queryKey: ['search', debounced], queryFn: () => api.get<Results>(`/api/search${qs({ q: debounced })}`), enabled: debounced.length >= 2 });
  const d = res.data;
  const empty = d && !d.jobs.length && !d.operations.length && !d.resources.length;
  return (
    <div className="search" ref={box}>
      <Search size={15} />
      <input placeholder="Search jobs, products, people   /" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} aria-label="Search" />
      {open && debounced.length >= 2 && (
        <div className="search-pop">
          {res.isLoading && <div className="empty">Searching…</div>}
          {empty && <div className="empty">No matches for “{debounced}”.</div>}
          {!!d?.jobs.length && <div className="grp">Jobs</div>}
          {d?.jobs.map((j) => (
            <button key={j.id} onClick={() => { drawer.open({ jobId: j.id }); setOpen(false); }}>
              <b title={j.reference}>{partOf(j)}</b> <span>{j.name}</span><span className="faint">{j.productCode ? j.reference : ''}{j.salesOrderRef ? `${j.productCode ? ' · ' : ''}${j.salesOrderRef}` : ''}</span>
              <span className="spacer" /><span className="badge">{STATUS_LABEL[j.status]}</span><span className="faint nowrap">{fmtDate(j.requiredDate)}</span>
            </button>
          ))}
          {!!d?.operations.length && <div className="grp">Operations</div>}
          {d?.operations.map((o) => (
            <button key={o.id} onClick={() => { drawer.open({ jobId: o.jobId, operationId: o.id }); setOpen(false); }}>
              <b title={o.reference}>{partOf(o)}</b> {o.name}<span className="spacer" /><span className="badge">{STATUS_LABEL[o.status]}</span>
            </button>
          ))}
          {!!d?.resources.length && <div className="grp">People</div>}
          {d?.resources.map((r) => (
            <button key={r.id} onClick={() => { nav(`/planner?resource=${r.id}`); setOpen(false); }}>
              <span className="avatar" style={{ width: 22, height: 22, fontSize: 10 }}>{r.initials}</span>{r.name}{!r.active && <span className="badge">inactive</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
